import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { setChatbotMode } from '../api/workforce/workforce'
import { buildFormData } from '../api/api'
import {
  ChevronDownIcon,
  LogOut03Icon,
  Pencil02Icon,
  RefreshCCW04Icon,
  Settings01Icon,
  SearchLgIcon,
} from '../assets/icons'
import { AuthContext } from '../hooks/auth/useAuth'
import LazyLoadImg from './image/LazyLoadImg'
import NavigationTab from './NavigationTab'
import NavigationBar from './Navbar'
import { useTranslation } from 'react-i18next'
import { Menu } from 'lucide-react'
import { prefetchPathIntent } from '../lib/performance/prefetch'

import { useRole } from '../hooks/auth/useRole'

const routeTitles: { prefix: string; titleKey: string; subtitleKey: string }[] = [
  { prefix: '/user/home', titleKey: 'navigation.home', subtitleKey: 'topBar.subtitles.home' },
  { prefix: '/user/todo', titleKey: 'navigation.todo', subtitleKey: 'topBar.subtitles.todo' },
  { prefix: '/user/workforce', titleKey: 'navigation.workforce', subtitleKey: 'topBar.subtitles.workforce' },
  { prefix: '/user/planning', titleKey: 'navigation.planning', subtitleKey: 'topBar.subtitles.planning' },
  { prefix: '/user/content', titleKey: 'navigation.createLibrary', subtitleKey: 'topBar.subtitles.createLibrary' },
  { prefix: '/user/explore', titleKey: 'navigation.explore', subtitleKey: 'topBar.subtitles.explore' },
  { prefix: '/user/payroll', titleKey: 'navigation.payroll', subtitleKey: 'topBar.subtitles.payroll' },
  { prefix: '/user/classification', titleKey: 'navigation.classifications', subtitleKey: 'topBar.subtitles.classifications' },
  { prefix: '/user/admin/document', titleKey: 'navigation.documents', subtitleKey: 'topBar.subtitles.documents' },
  { prefix: '/user/document', titleKey: 'documents.labels.hrDocuments', subtitleKey: 'topBar.subtitles.hrDocuments' },
  { prefix: '/user/settings', titleKey: 'navigation.settings', subtitleKey: 'topBar.subtitles.settings' },
  { prefix: '/user/profile', titleKey: 'navigation.myProfile', subtitleKey: 'topBar.subtitles.myProfile' },
  { prefix: '/user/organization', titleKey: 'navigation.organization', subtitleKey: 'topBar.subtitles.organization' },
  { prefix: '/user/evaluations', titleKey: 'navigation.evaluations', subtitleKey: 'topBar.subtitles.evaluations' },
  { prefix: '/user/analytics', titleKey: 'navigation.analytics', subtitleKey: 'topBar.subtitles.analytics' },
  { prefix: '/user/outflow', titleKey: 'navigation.outflow', subtitleKey: 'topBar.subtitles.outflow' },
  { prefix: '/user/recruitment', titleKey: 'navigation.recruitment', subtitleKey: 'topBar.subtitles.recruitment' },
  { prefix: '/user/chat-v2', titleKey: 'navigation.aiAssistant', subtitleKey: 'topBar.subtitles.aiAssistant' },
  { prefix: '/user/chat', titleKey: 'navigation.aiAssistant', subtitleKey: 'topBar.subtitles.aiAssistant' },
  { prefix: '/user/hr-chat', titleKey: 'navigation.hrChat', subtitleKey: 'topBar.subtitles.hrChat' },
  { prefix: '/user/faq', titleKey: 'navigation.faq', subtitleKey: 'topBar.subtitles.faq' },
  { prefix: '/user/notifications', titleKey: 'navigation.notifications', subtitleKey: 'topBar.subtitles.notifications' },
  { prefix: '/user/tickets', titleKey: 'navigation.tickets', subtitleKey: 'topBar.subtitles.tickets' },
]

const getPageInfo = (pathname: string, t: TFunction): { title: string; subtitle: string } => {
  const match = routeTitles.find((r) => pathname.startsWith(r.prefix))
  if (match) {
    return { title: t(match.titleKey), subtitle: t(match.subtitleKey) }
  }
  // Fallback: derive title from URL path
  const segment = pathname.split('/').filter(Boolean).pop() || ''

  const fallbackTitle = segment
    // insert space before capital letters (for future-proofing)
    .replace(/([A-Z])/g, ' $1')
    // handle fully lowercase words like addcontentitems → add content items
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // fallback: split common words manually if no capitals
    .replace(/(add)(content)(items)/i, '$1 $2 $3')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase())

  return { title: fallbackTitle, subtitle: '' }
  }

interface MenuOptionProps {
  icon: React.ElementType
  label: string
  onClick: () => void
}

const MenuOption: React.FC<MenuOptionProps> = ({ icon: IconComponent, label, onClick }) => (
  <Button variant="topMenu" justifyContent="flex-start" onClick={onClick} px="12px">
    <Flex direction="row" alignItems="center" gap="10px">
      <Icon boxSize="18px" viewBox="0 0 24 24" color="solid.400">
        <IconComponent />
      </Icon>
      <Text textStyle="l-s" color="solid.600">
        {label}
      </Text>
    </Flex>
  </Button>
)

interface TabItem {
  title: string
  path?: string
  tab: string
}

interface PageHeaderProps {
  tabConfig: TabItem[] | null
  selectedTab: string
  onSelectTab: (tab: string, path?: string) => void
  onSearchClick?: () => void
}

const PageHeader: React.FC<PageHeaderProps> = ({ tabConfig, selectedTab, onSelectTab, onSearchClick }) => {
  const { t } = useTranslation()
  const { user, logout, updateUserCredentials } = useContext(AuthContext)
  const toast = useToast()
  const queryClient = useQueryClient()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const pageInfo = getPageInfo(location.pathname, t)
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()
  const { isAdmin, backendRole, backendIsAdminCapable } = useRole()

  useEffect(() => {
    if (!tabConfig?.length) return

    const selectedIndex = tabConfig.findIndex((tab) => tab.tab === selectedTab)
    const fallbackIndex = selectedIndex >= 0 ? selectedIndex : 0
    const candidates = [tabConfig[fallbackIndex - 1], tabConfig[fallbackIndex + 1]]
      .filter((tab): tab is TabItem => Boolean(tab?.path))

    if (!candidates.length) return

    if (typeof window === 'undefined') return

    const schedule: (cb: () => void) => number =
      typeof window.requestIdleCallback === 'function'
        ? (cb) => window.requestIdleCallback(cb)
        : (cb) => window.setTimeout(cb, 120)

    const cancel: (handle: number) => void =
      typeof window.cancelIdleCallback === 'function'
        ? (handle) => window.cancelIdleCallback(handle)
        : (handle) => window.clearTimeout(handle)

    const handle = schedule(() => {
      candidates.forEach((tab) => {
        if (tab.path) prefetchPathIntent(tab.path, queryClient)
      })
    })

    return () => cancel(handle)
  }, [tabConfig, selectedTab, queryClient])

  const handleLogout = async () => {
    localStorage.clear()
    await logout()
  }

  const ALLOWED_PFP_TYPES = ['image/png', 'image/jpeg']
  const MAX_PFP_SIZE_MB = 5

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ALLOWED_PFP_TYPES.includes(file.type)) {
      toast({
        title: t('workforce.people.profilePicture.unsupportedFormat'),
        description: t('workforce.people.profilePicture.unsupportedFormatMessage'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom',
      })
      return
    }

    if (file.size > MAX_PFP_SIZE_MB * 1024 * 1024) {
      toast({
        title: t('workforce.people.profilePicture.sizeExceeded'),
        description: t('workforce.people.profilePicture.sizeExceededMessage', { size: MAX_PFP_SIZE_MB }),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom',
      })
      return
    }

    const formData = buildFormData({ profile_picture: file })
    if (user?.id) {
      updateUserCredentials(formData)
    }
  }

  const handleRoleSwitch = async () => {
    if (!user || !backendIsAdminCapable) return

    const nextIsAdmin = !isAdmin
    const nextChatbotMode = nextIsAdmin ? 'admin' : 'user'

    // Remove current-self query immediately so UserWrapper renders <Loading />
    // (removeQueries does not refetch, so the loading state persists until navigation)
    queryClient.removeQueries({ queryKey: ['current-self'], exact: true })

    try {
      await setChatbotMode(nextChatbotMode)
      localStorage.setItem('is_admin', nextIsAdmin ? 'true' : 'false')
      localStorage.setItem('role', nextIsAdmin ? (backendRole || 'user') : 'user')
      window.location.href = `${window.location.origin}/user/home`
    } catch (error) {
      toast({
        title: t('common.status.error'),
        description: error instanceof Error ? error.message : 'Failed to switch role view',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom',
      })
    }
  }

  const handleSettings = async () => {
    setIsMenuOpen(false)
    if (isAdmin && user) {
      queryClient.removeQueries({ queryKey: ['current-self'], exact: true })
      await setChatbotMode('user')
      localStorage.setItem('is_admin', 'false')
      localStorage.setItem('role', 'user')
      window.location.href = `${window.location.origin}/user/profile?tab=settings`
    } else {
      navigate('/user/profile?tab=settings')
    }
  }

  const openFilePicker = () => fileInputRef.current?.click()

  const renderAvatar = (size: number, clickable = false) => {
    const props = clickable
      ? { onClick: openFilePicker, cursor: 'pointer' as const }
      : {}

    if (user?.profile_picture) {
      return (
        <LazyLoadImg
          src={user.profile_picture}
          alt={user?.first_name}
          width={size}
          height={size}
          borderRadius="50%"
          objectFit="cover"
          style={{ display: 'block', maxWidth: size, maxHeight: size }}
          {...props}
        />
      )
    }
    return <Avatar boxSize={`${size}px`} {...props} />
  }

  return (
    <Box px={{ base: '16px', md: '32px' }} pt={{ base: '16px', md: '24px' }} pb={location.pathname.startsWith('/user/home') ? '20px' : '12px'}>
      {/* Top row: hamburger (mobile) + title + user menu */}
      <Flex justifyContent="space-between" alignItems="center" mb={tabConfig ? '12px' : '0'}>
        <Flex alignItems="center" gap="12px" minW={0} flex="1">
          {/* Mobile hamburger */}
          <IconButton
            aria-label="Open navigation"
            icon={<Menu size={20} />}
            variant="ghost"
            display={{ base: 'flex', md: 'none' }}
            onClick={onDrawerOpen}
            size="sm"
          />

          {/* Title + subtitle */}
          <Flex direction="column" gap="2px" minW={0}>
            <Text textStyle="h-lg" color="solid.900" lineHeight="24px" noOfLines={1}>
              {location.pathname.startsWith('/user/home')
                ? `${t('dashboard.greeting.welcome', { name: user?.first_name || '' })} \u{1F44B}`
                : pageInfo.title}
            </Text>
            <Text textStyle="l-xs" color="solid.400" lineHeight="16px" noOfLines={1}>
              {location.pathname.startsWith('/user/home')
                ? t('dashboard.greeting.subtitle')
                : pageInfo.subtitle}
            </Text>
          </Flex>
        </Flex>

        {/* Right side: search + user menu */}
        <Flex align="center" gap="12px">
          {/* Search button */}
          <Button
            variant="noEffect"
            onClick={onSearchClick}
            h="36px"
            px="12px"
            borderRadius="10px"
            bg="white"
            border="1px solid"
            borderColor="solid.50"
            boxShadow="0px 1px 3px rgba(0, 0, 0, 0.04)"
            _hover={{ borderColor: 'primary.25', boxShadow: '0px 2px 8px rgba(93, 94, 203, 0.08)' }}
            transition="all 0.2s ease-out"
          >
            <Flex align="center" gap="8px">
              <Icon width="16px" height="16px" fill="none" color="solid.400">
                <SearchLgIcon />
              </Icon>
              <Text fontSize="13px" fontWeight="400" color="solid.400" display={{ base: 'none', md: 'block' }}>
                Search...
              </Text>
              <Flex align="center" gap="2px" display={{ base: 'none', md: 'flex' }}>
                <Text
                  fontSize="11px"
                  fontWeight="500"
                  color="solid.300"
                  bg="solid.25"
                  px="5px"
                  py="1px"
                  borderRadius="4px"
                  border="1px solid"
                  borderColor="solid.100"
                >
                  ⌘K
                </Text>
              </Flex>
            </Flex>
          </Button>

          {/* User popover menu */}
          <Popover isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} placement="bottom-end">
            <PopoverTrigger>
              <Button
                variant="noEffect"
                onClick={() => setIsMenuOpen((prev) => !prev)}
                px="4px"
                py="4px"
                h="auto"
                borderRadius="40px"
                bg={isMenuOpen ? 'white' : 'transparent'}
                boxShadow={isMenuOpen ? '0px 2px 8px rgba(93, 94, 203, 0.10)' : 'none'}
                _hover={{ bg: 'white', boxShadow: '0px 2px 8px rgba(93, 94, 203, 0.08)' }}
                transition="all 0.2s ease-out"
              >
                <Flex alignItems="center" gap="8px" pr="4px">
                  {renderAvatar(32)}
                  <Text fontWeight="600" fontSize="14px" color="solid.900" display={{ base: 'none', sm: 'block' }}>
                    {user?.first_name} {user?.last_name}
                  </Text>
                  <Icon
                    width="16px"
                    height="16px"
                    fill="none"
                    color={isMenuOpen ? 'primary.100' : 'solid.400'}
                    transition="transform 0.2s ease, color 0.2s ease"
                    transform={isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                    display={{ base: 'none', sm: 'block' }}
                  >
                    <ChevronDownIcon />
                  </Icon>
                </Flex>
              </Button>
            </PopoverTrigger>
            <PopoverContent w="260px" mt="6px">
              <PopoverBody py="12px" px="8px">
                <Flex alignItems="center" px="8px" pb="12px" gap="10px">
                  <Flex position="relative" cursor="pointer" flexShrink={0} _hover={{ '.overlay': { opacity: 1 } }}>
                    {renderAvatar(40, true)}
                    <Box
                      className="overlay"
                      position="absolute"
                      top="0"
                      left="0"
                      width="100%"
                      height="100%"
                      bg="blackAlpha.500"
                      opacity="0"
                      pointerEvents="none"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="full"
                      transition="opacity 0.2s ease-in-out"
                    >
                      <Icon boxSize="16px" viewBox="0 0 24 24" color="white">
                        <Pencil02Icon />
                      </Icon>
                    </Box>
                  </Flex>
                  <Flex direction="column" minW={0}>
                    <Text fontWeight="600" fontSize="14px" color="solid.900" noOfLines={1}>
                      {user?.first_name} {user?.last_name}
                    </Text>
                    <Text textStyle="l-xs" color="primary.100" lineHeight="14px">
                      {isAdmin ? t('topBar.roles.admin') : t('common.fields.user')}
                    </Text>
                  </Flex>
                </Flex>

                <Divider borderColor="solid.50" my="0" />

                <Flex mt="8px" direction="column" gap="2px">
                  {backendIsAdminCapable && (
                    <MenuOption
                      icon={RefreshCCW04Icon}
                      label={t('topBar.roles.switchTo', {
                        role: isAdmin ? t('common.fields.user') : t('topBar.roles.admin'),
                      })}
                      onClick={handleRoleSwitch}
                    />
                  )}
                  <MenuOption icon={Settings01Icon} label={t('navigation.settings')} onClick={handleSettings} />
                  <MenuOption icon={LogOut03Icon} label={t('auth.actions.logout')} onClick={handleLogout} />
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Flex>
      </Flex>

      {/* Tabs row (optional) */}
      {tabConfig && (
        <Box overflowX={{ base: 'auto', md: 'visible' }} whiteSpace="nowrap" css={{
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}>
          <NavigationTab selectedTab={selectedTab} onSelectTab={onSelectTab} tabItems={tabConfig} />
        </Box>
      )}

      {/* Mobile navigation drawer */}
      <Drawer isOpen={isDrawerOpen} placement="left" onClose={onDrawerClose} size="xs">
        <DrawerOverlay />
        <DrawerContent maxW="250px">
          <DrawerCloseButton />
          <DrawerBody p={0}>
            <NavigationBar isInDrawer onNavigate={onDrawerClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg"
        style={{ display: 'none' }}
        onChange={handleAvatarUpload}
      />
    </Box>
  )
}

export default PageHeader
